/**
 * Remote push for the native app (iOS/Android) via APNs/FCM.
 *
 * The browser uses Web Push (see NotificationBell); inside the Capacitor app
 * Web Push never fires, so the phone registers a device token instead and the
 * server pushes to it. No-op on web.
 *
 * Registration is retried whenever the app comes back to the foreground: the
 * user often grants notification permission after the first launch (e.g. from
 * the bell panel), and without a retry that device would never get a token.
 *
 * Every outcome is reported to /push/diagnostics, because a phone that fails
 * here is otherwise completely silent — no server log, no visible error.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/lib/api';

let registered = false;
let inFlight = false;

async function report(stage, detail) {
  try {
    await api.post('/push/diagnostics', {
      stage,
      detail: String(detail ?? '').slice(0, 300),
      platform: Capacitor.getPlatform(),
    });
  } catch {
    // Diagnostics must never break registration.
  }
}

/**
 * Register this device with the server if notifications are allowed.
 * Safe to call repeatedly: it only registers once, and stays retryable while
 * permission is still missing. Returns a cleanup function.
 */
export async function registerNativePush(onOpenPath) {
  if (!Capacitor.isNativePlatform() || registered || inFlight) return () => {};
  inFlight = true;
  try {
    if (!Capacitor.isPluginAvailable('PushNotifications')) {
      await report('plugin-missing', 'PushNotifications not available on this build');
      return () => {};
    }

    let status = await PushNotifications.checkPermissions();
    if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
      status = await PushNotifications.requestPermissions();
    }
    // Not granted (yet): leave `registered` false so the next resume retries.
    if (status.receive !== 'granted') {
      await report('permission', status.receive);
      return () => {};
    }

    const handles = [
      PushNotifications.addListener('registration', async ({ value }) => {
        try {
          await api.post('/push/device', { token: value, platform: Capacitor.getPlatform() });
        } catch (e) {
          // The token is re-delivered on the next register(), so a failure here
          // is recoverable — but drop the flag so we actually try again.
          registered = false;
          await report('post-failed', e?.message || e);
        }
      }),
      PushNotifications.addListener('registrationError', async (e) => {
        registered = false;
        await report('apns-error', e?.error || JSON.stringify(e));
      }),
      PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const path = notification?.data?.url;
        if (path) onOpenPath(path);
      }),
    ];

    await PushNotifications.register();
    registered = true;
    await report('register-called', 'waiting for the token');

    return () => {
      handles.forEach((h) => h.then((listener) => listener.remove()).catch(console.error));
    };
  } catch (e) {
    await report('exception', e?.message || e);
    return () => {};
  } finally {
    inFlight = false;
  }
}
